import { CommonModule } from "@angular/common";
import { Component, ElementRef, HostListener, Input, ViewChild } from "@angular/core";

type MediaType = "image" | "video";

@Component({
  selector: "app-media-preview",
  standalone: true,
  imports: [CommonModule],
  templateUrl: "./media-preview.component.html",
  styleUrl: "./media-preview.component.css",
})
export class MediaPreviewComponent {
  @Input() url: string | null | undefined = "";
  @Input() label = "Media preview";

  @ViewChild("viewerContainer") viewerContainer?: ElementRef<HTMLElement>;

  isViewerOpen = false;
  zoomLevel = 1;

  get previewUrl(): string {
    const value = String(this.url || "").trim();
    if (!value || value.toUpperCase() === "NA") {
      return "";
    }
    return value;
  }

  get hasPreview(): boolean {
    return !!this.previewUrl;
  }

  get mediaType(): MediaType {
    const value = this.previewUrl.toLowerCase();
    const videoExt = [".mp4", ".webm", ".mov", ".m4v", ".avi"];
    const isVideo = videoExt.some((ext) => value.includes(ext));
    return isVideo ? "video" : "image";
  }

  get isImage(): boolean {
    return this.mediaType === "image";
  }

  get isVideo(): boolean {
    return this.mediaType === "video";
  }

  get zoomPercent(): number {
    return Math.round(this.zoomLevel * 100);
  }

  openViewer() {
    if (!this.hasPreview) return;
    this.isViewerOpen = true;
    this.zoomLevel = 1;
  }

  closeViewer() {
    this.isViewerOpen = false;
    this.zoomLevel = 1;
  }

  openInNewTab(event?: Event) {
    event?.stopPropagation();
    if (!this.previewUrl) return;
    window.open(this.previewUrl, "_blank", "noopener,noreferrer");
  }

  zoomIn(event?: Event) {
    event?.stopPropagation();
    if (!this.isImage) return;
    this.zoomLevel = Math.min(4, Number((this.zoomLevel + 0.25).toFixed(2)));
  }

  zoomOut(event?: Event) {
    event?.stopPropagation();
    if (!this.isImage) return;
    this.zoomLevel = Math.max(1, Number((this.zoomLevel - 0.25).toFixed(2)));
  }

  requestFullscreen(event?: Event) {
    event?.stopPropagation();
    const container = this.viewerContainer?.nativeElement as any;
    if (!container) return;
    if (container.requestFullscreen) {
      container.requestFullscreen();
      return;
    }
    if (container.webkitRequestFullscreen) {
      container.webkitRequestFullscreen();
    }
  }

  @HostListener("document:keydown.escape")
  onEsc() {
    if (this.isViewerOpen) {
      this.closeViewer();
    }
  }
}

